// ---------------------------------------------------------------------------
// C5+C6: Version Timeline + Branching
// ---------------------------------------------------------------------------
// C5: Timeline slider — every diff = version dot. Hover → preview.
//     Click → restore (non-destructive). Star → named checkpoint.
// C6: Branch from any version → parallel chain. Compare side-by-side.
//     Cherry-pick sections from different branches → merge.
//
// Builds on Sprint 11 ConversationState.versions.
// ---------------------------------------------------------------------------

import type { PuckState, PuckComponent } from "../ai/puck-types.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TimelineVersion {
  id: string;
  branchId: string;
  versionNumber: number;
  state: PuckState;
  description: string;
  /** Named checkpoint (star) */
  checkpoint?: string;
  /** User or AI action that created this version */
  action: string;
  timestamp: number;
  /** Parent version (for branch tracking) */
  parentVersionId: string | null;
}

export interface Branch {
  id: string;
  name: string;
  /** Version this branch was created from */
  branchPointVersionId: string;
  createdAt: number;
  /** Whether this is the main branch */
  isMain: boolean;
}

export interface Timeline {
  branches: Branch[];
  versions: TimelineVersion[];
  activeBranchId: string;
  activeVersionId: string;
}

export interface VersionComparison {
  versionA: TimelineVersion;
  versionB: TimelineVersion;
  /** Components only in A */
  onlyInA: PuckComponent[];
  /** Components only in B */
  onlyInB: PuckComponent[];
  /** Components in both but with different props */
  changed: Array<{ componentId: string; type: string; propsA: Record<string, unknown>; propsB: Record<string, unknown> }>;
  /** Components identical in both */
  unchanged: number;
}

export interface CherryPickResult {
  /** New version created by cherry-picking */
  version: TimelineVersion;
  /** Components picked from source */
  pickedComponents: string[];
}

// ---------------------------------------------------------------------------
// Timeline Management
// ---------------------------------------------------------------------------

/**
 * Create a new timeline with an initial version on the main branch.
 */
export function createTimeline(initialState: PuckState): Timeline {
  const mainBranch: Branch = {
    id: "main",
    name: "Main",
    branchPointVersionId: "",
    createdAt: Date.now(),
    isMain: true,
  };

  const initialVersion: TimelineVersion = {
    id: crypto.randomUUID(),
    branchId: "main",
    versionNumber: 0,
    state: initialState,
    description: "Initial state",
    action: "create",
    timestamp: Date.now(),
    parentVersionId: null,
  };

  mainBranch.branchPointVersionId = initialVersion.id;

  return {
    branches: [mainBranch],
    versions: [initialVersion],
    activeBranchId: "main",
    activeVersionId: initialVersion.id,
  };
}

/**
 * Add a new version to the active branch.
 */
export function addVersion(
  timeline: Timeline,
  state: PuckState,
  description: string,
  action: string,
): Timeline {
  const parentId = timeline.activeVersionId;
  const branchVersions = timeline.versions.filter(v => v.branchId === timeline.activeBranchId);
  const nextNumber = branchVersions.length;

  const newVersion: TimelineVersion = {
    id: crypto.randomUUID(),
    branchId: timeline.activeBranchId,
    versionNumber: nextNumber,
    state,
    description,
    action,
    timestamp: Date.now(),
    parentVersionId: parentId,
  };

  return {
    ...timeline,
    versions: [...timeline.versions, newVersion],
    activeVersionId: newVersion.id,
  };
}

/**
 * C5: Star a version as a named checkpoint.
 */
export function starVersion(timeline: Timeline, versionId: string, checkpointName: string): Timeline {
  return {
    ...timeline,
    versions: timeline.versions.map(v =>
      v.id === versionId ? { ...v, checkpoint: checkpointName } : v,
    ),
  };
}

/**
 * C5: Restore a version (non-destructive — creates new version = copy).
 */
export function restoreVersion(timeline: Timeline, targetVersionId: string): Timeline {
  const target = timeline.versions.find(v => v.id === targetVersionId);
  if (!target) return timeline;

  return addVersion(
    timeline,
    target.state,
    `Restored from v${target.versionNumber} (${target.description})`,
    "restore",
  );
}

/**
 * C5: Navigate to a version for preview (doesn't create new version).
 */
export function navigateToVersion(timeline: Timeline, versionId: string): Timeline {
  if (!timeline.versions.find(v => v.id === versionId)) return timeline;
  return { ...timeline, activeVersionId: versionId };
}

// ---------------------------------------------------------------------------
// C6: Branching
// ---------------------------------------------------------------------------

/**
 * Create a branch from a specific version.
 */
export function createBranch(
  timeline: Timeline,
  fromVersionId: string,
  branchName: string,
): Timeline {
  const source = timeline.versions.find(v => v.id === fromVersionId);
  if (!source) return timeline;

  const branchId = `branch-${crypto.randomUUID().slice(0, 8)}`;
  const newBranch: Branch = {
    id: branchId,
    name: branchName,
    branchPointVersionId: fromVersionId,
    createdAt: Date.now(),
    isMain: false,
  };

  // Create initial version on the new branch (copy of source state)
  const branchVersion: TimelineVersion = {
    id: crypto.randomUUID(),
    branchId,
    versionNumber: 0,
    state: structuredClone(source.state),
    description: `Branch "${branchName}" from v${source.versionNumber}`,
    action: "branch",
    timestamp: Date.now(),
    parentVersionId: fromVersionId,
  };

  return {
    ...timeline,
    branches: [...timeline.branches, newBranch],
    versions: [...timeline.versions, branchVersion],
    activeBranchId: branchId,
    activeVersionId: branchVersion.id,
  };
}

/**
 * Switch to a different branch.
 */
export function switchBranch(timeline: Timeline, branchId: string): Timeline {
  const branch = timeline.branches.find(b => b.id === branchId);
  if (!branch) return timeline;

  // Find the latest version on this branch
  const branchVersions = timeline.versions
    .filter(v => v.branchId === branchId)
    .sort((a, b) => b.versionNumber - a.versionNumber);

  const latestVersion = branchVersions[0];
  if (!latestVersion) return timeline;

  return {
    ...timeline,
    activeBranchId: branchId,
    activeVersionId: latestVersion.id,
  };
}

/**
 * C6: Compare two versions side-by-side.
 */
export function compareVersions(
  timeline: Timeline,
  versionIdA: string,
  versionIdB: string,
): VersionComparison | null {
  const vA = timeline.versions.find(v => v.id === versionIdA);
  const vB = timeline.versions.find(v => v.id === versionIdB);
  if (!vA || !vB) return null;

  const mapA = new Map(vA.state.content.map(c => [c._id ?? c.type, c]));
  const mapB = new Map(vB.state.content.map(c => [c._id ?? c.type, c]));

  const onlyInA: PuckComponent[] = [];
  const onlyInB: PuckComponent[] = [];
  const changed: VersionComparison["changed"] = [];
  let unchanged = 0;

  for (const [id, compA] of mapA) {
    const compB = mapB.get(id);
    if (!compB) {
      onlyInA.push(compA);
    } else if (JSON.stringify(compA.props) !== JSON.stringify(compB.props)) {
      changed.push({ componentId: id, type: compA.type, propsA: compA.props, propsB: compB.props });
    } else {
      unchanged++;
    }
  }

  for (const [id, compB] of mapB) {
    if (!mapA.has(id)) onlyInB.push(compB);
  }

  return { versionA: vA, versionB: vB, onlyInA, onlyInB, changed, unchanged };
}

/**
 * C6: Cherry-pick specific components from one version into another.
 * Creates a new version with the picked components replacing their counterparts.
 */
export function cherryPick(
  timeline: Timeline,
  sourceVersionId: string,
  componentIds: string[],
): Timeline {
  const source = timeline.versions.find(v => v.id === sourceVersionId);
  const current = timeline.versions.find(v => v.id === timeline.activeVersionId);
  if (!source || !current) return timeline;

  const pickSet = new Set(componentIds);
  const sourceMap = new Map(source.state.content.map(c => [c._id ?? c.type, c]));

  // Replace matching components in current state with source versions
  const newContent = current.state.content.map(c => {
    const id = c._id ?? c.type;
    if (pickSet.has(id) && sourceMap.has(id)) {
      return structuredClone(sourceMap.get(id)!);
    }
    return c;
  });

  // Add components from source that don't exist in current
  for (const id of componentIds) {
    const sourceComp = sourceMap.get(id);
    if (sourceComp && !current.state.content.some(c => (c._id ?? c.type) === id)) {
      newContent.push(structuredClone(sourceComp));
    }
  }

  return addVersion(
    timeline,
    { ...current.state, content: newContent },
    `Cherry-picked ${componentIds.length} component(s) from ${source.description}`,
    "cherry-pick",
  );
}

/**
 * Get versions for a specific branch, sorted by version number.
 */
export function getBranchVersions(timeline: Timeline, branchId: string): TimelineVersion[] {
  return timeline.versions
    .filter(v => v.branchId === branchId)
    .sort((a, b) => a.versionNumber - b.versionNumber);
}

/**
 * Get the active version's state.
 */
export function getActiveState(timeline: Timeline): PuckState {
  const active = timeline.versions.find(v => v.id === timeline.activeVersionId);
  return active?.state ?? { content: [] };
}
